import * as Common from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  createColumnHelper,
  DataTable,
  Empty,
  type PaginationOptions,
  type RowSelectionOptions,
} from '@piccolohealth/ui';
import React from 'react';
import { LabelsStack } from '../../components/generic/LabelsStack';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { ParticipantStatusTag } from '../participant/ParticipantStatusTag';

interface Props {
  user: Common.User;
  participants: Common.Participant[];
  isLoading: boolean;
  error: Common.PiccoloError | null;
  refetch: () => Promise<void>;
  pagination?: PaginationOptions;
  rowSelection?: RowSelectionOptions<Common.Participant>;
}

export const UserParticipantsAddTable = (props: Props) => {
  const columns = React.useMemo(() => {
    const columnHelper = createColumnHelper<Common.Participant>();

    return [
      columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
        id: 'name',
        header: 'Name',
        minSize: 160,
        maxSize: 200,
        cell: (ps) => (
          <ChakraV3.Text>{Common.renderParticipantName(ps.row.original)}</ChakraV3.Text>
        ),
      }),

      columnHelper.display({
        header: 'Assigned',
        id: 'hasAccess',
        minSize: 100,
        maxSize: 140,
        cell: (ps) => {
          const hasAccess = ps.row.original.assignedUsers.some((u) => u.user.id === props.user.id);
          return hasAccess ? (
            <ChakraV3.Text>Already assigned</ChakraV3.Text>
          ) : (
            <ChakraV3.Text>Not assigned</ChakraV3.Text>
          );
        },
      }),
      columnHelper.accessor('location.name', {
        header: 'Location',
        minSize: 200,
        maxSize: 240,
        cell: (ps) => (
          <ChakraV3.Tag size='sm' colorPalette={ps.row.original.location.color}>
            {ps.row.original.location.name}
          </ChakraV3.Tag>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        minSize: 100,
        maxSize: 130,
        cell: (ps) => <ParticipantStatusTag status={ps.row.original.status} size='sm' />,
      }),
      columnHelper.accessor('label.name', {
        header: 'Labels',
        cell: (ps) => <LabelsStack labels={ps.row.original.labels} />,
      }),
    ];
  }, [props.user.id]);

  return (
    <DataTable
      columns={columns}
      data={props.participants}
      size='sm'
      error={props.error}
      isLoading={props.isLoading}
      pagination={props.pagination}
      rowSelection={props.rowSelection}
      renderError={(error) => <PiccoloError error={error} />}
      renderEmpty={() => <Empty title='No participants found' />}
    />
  );
};
